/**
 * ShareCard — generate a cinematic branded PNG for a finding and
 * trigger download. Pure client-side canvas, no backend.
 *
 * 1200 × 630 — the de-facto Twitter/LinkedIn/OG card size.
 */

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let cur = "";
  for (const w of words) {
    const test = cur ? cur + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

export async function generateShareCard(finding) {
  const W = 1200;
  const H = 630;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // bg
  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(0, 0, W, H);

  // corner glow
  const g1 = ctx.createRadialGradient(W - 180, H - 180, 0, W - 180, H - 180, 520);
  g1.addColorStop(0, "rgba(255, 77, 0, 0.35)");
  g1.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g1;
  ctx.fillRect(0, 0, W, H);

  const g2 = ctx.createRadialGradient(120, 120, 0, 120, 120, 420);
  g2.addColorStop(0, "rgba(0, 240, 255, 0.18)");
  g2.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g2;
  ctx.fillRect(0, 0, W, H);

  // grid
  ctx.strokeStyle = "rgba(255,255,255,0.04)";
  ctx.lineWidth = 1;
  for (let x = 0; x < W; x += 56) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, H);
    ctx.stroke();
  }
  for (let y = 0; y < H; y += 56) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
  }

  // border
  ctx.strokeStyle = "rgba(255,77,0,0.35)";
  ctx.lineWidth = 2;
  ctx.strokeRect(40, 40, W - 80, H - 80);

  // ember dots (static)
  for (let i = 0; i < 60; i++) {
    const x = Math.random() * W;
    const y = Math.random() * H;
    const r = Math.random() * 1.8 + 0.4;
    const a = Math.random() * 0.6 + 0.1;
    ctx.fillStyle =
      Math.random() < 0.85
        ? `rgba(255, 120, 40, ${a})`
        : `rgba(0, 240, 255, ${a * 0.7})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // brand pill
  ctx.fillStyle = "rgba(255, 77, 0, 0.08)";
  roundRect(ctx, 80, 80, 470, 52, 0);
  ctx.fill();
  ctx.strokeStyle = "#FF4D00";
  ctx.lineWidth = 1;
  ctx.strokeRect(80, 80, 470, 52);
  ctx.fillStyle = "#FF4D00";
  ctx.font =
    "600 18px 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
  ctx.textBaseline = "middle";
  ctx.fillText("◆  PROMETHEUS·EBM   /   RESEARCH UPDATE", 100, 107);

  // tag + date
  ctx.fillStyle = "#00F0FF";
  ctx.font =
    "500 16px 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
  ctx.fillText(
    `${(finding.tag || "").toUpperCase()}   ·   ${finding.date || ""}`,
    80,
    180,
  );

  // title
  ctx.fillStyle = "#F5F5F5";
  ctx.font =
    "600 58px 'Cormorant Garamond', Georgia, 'Times New Roman', serif";
  ctx.textBaseline = "top";
  const titleLines = wrapText(ctx, finding.title || "", W - 160);
  let y = 220;
  for (const l of titleLines.slice(0, 3)) {
    ctx.fillText(l, 80, y);
    y += 70;
  }

  // summary
  ctx.fillStyle = "#A3A3A3";
  ctx.font =
    "400 22px 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
  const sumLines = wrapText(ctx, finding.summary || "", W - 160);
  y = Math.min(y + 30, H - 170);
  for (const l of sumLines.slice(0, 3)) {
    ctx.fillText(l, 80, y);
    y += 34;
  }

  // footer strip
  ctx.strokeStyle = "rgba(255,77,0,0.5)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(80, H - 110);
  ctx.lineTo(W - 80, H - 110);
  ctx.stroke();

  ctx.fillStyle = "#737373";
  ctx.font =
    "500 16px 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
  ctx.fillText("prometheus-ebm   ·   pypi · kaggle · github", 80, H - 90);

  ctx.fillStyle = "#FF4D00";
  ctx.textAlign = "right";
  ctx.fillText("stolen fire · 2026", W - 80, H - 90);
  ctx.textAlign = "left";

  // download
  const url = canvas.toDataURL("image/png");
  const a = document.createElement("a");
  a.href = url;
  a.download = `prometheus-ebm-${finding.id || "share"}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  return url;
}
